/**
 * The password rules, as the server reports them.
 *
 * Three forms carried three different hand-written copies — "min 6" in the
 * account menu's modal, "min 8" in Settings, "min 8" on the reset page — and
 * none of them checked complexity at all, while the server requires an
 * uppercase letter, a lowercase letter, a digit, a symbol and a password that
 * is not on the common list. A password every form accepted was then rejected
 * after the round trip (QA ledger AUTH-008).
 *
 * Mirrors `validatePassword` in core-platform's `auth/password.ts`, which is
 * what every write path actually enforces; the policy itself is fetched rather
 * than assumed, so a relaxed local-development policy relaxes the forms too.
 */
/**
 * Used until the server's answer arrives, and if it never does.
 *
 * Deliberately the STRICT policy rather than a permissive one: a form that
 * guesses wrong should refuse a password the server would have taken, not
 * accept one it will reject.
 */
export const STRICT_PASSWORD_POLICY = {
    minLength: 8,
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSpecialChars: true,
    preventCommon: true,
};
/** Same list the server rejects on; kept in sync with core-platform. */
const COMMON_PASSWORDS = new Set([
    'password', 'password123', '12345678', 'qwerty', 'abc123', 'monkey',
    '1234567890', 'letmein', 'trustno1', 'dragon', 'baseball', 'iloveyou',
    'master', 'sunshine', 'ashley', 'bailey', 'passw0rd', 'shadow', '123123',
    '654321',
]);
const SPECIAL_CHARS = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;
/**
 * The policy as a checklist against a candidate password, so the form can show
 * what is still missing instead of one message at a time.
 */
export const describePasswordPolicy = (policy, password) => {
    const requirements = [
        {
            id: 'length',
            label: `At least ${policy.minLength} character${policy.minLength === 1 ? '' : 's'}`,
            met: password.length >= policy.minLength,
        },
    ];
    if (policy.requireUppercase) {
        requirements.push({ id: 'upper', label: 'An uppercase letter', met: /[A-Z]/.test(password) });
    }
    if (policy.requireLowercase) {
        requirements.push({ id: 'lower', label: 'A lowercase letter', met: /[a-z]/.test(password) });
    }
    if (policy.requireNumbers) {
        requirements.push({ id: 'number', label: 'A number', met: /\d/.test(password) });
    }
    if (policy.requireSpecialChars) {
        requirements.push({
            id: 'special',
            label: 'A symbol (!, @, #, …)',
            met: SPECIAL_CHARS.test(password),
        });
    }
    if (policy.preventCommon) {
        requirements.push({
            id: 'common',
            label: 'Not a commonly used password',
            // An empty box is not yet "a common password" — it would read as a
            // failure before anything has been typed.
            met: password.length > 0 && !COMMON_PASSWORDS.has(password.toLowerCase()),
        });
    }
    return requirements;
};
/** True when the candidate satisfies every requirement the policy states. */
export const meetsPasswordPolicy = (policy, password) => describePasswordPolicy(policy, password).every((requirement) => requirement.met);
/** The first unmet requirement, phrased as a sentence, or null when it passes. */
export const firstPasswordProblem = (policy, password) => {
    const unmet = describePasswordPolicy(policy, password).find((r) => !r.met);
    if (!unmet)
        return null;
    return unmet.id === 'common'
        ? 'That password is too common — please choose a stronger one.'
        : `Your password needs ${unmet.label.replace(/^A(n)? /, 'a$1 ').toLowerCase()}.`;
};
